import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import { Spinner, type SpinnerSize } from '@/shared/ui/Spinner'
import s from './IconButton.module.css'

/**
 * Громкость действия: `primary` — акцентная заливка (единичное быстрое действие),
 * `secondary` — стекло (кебаб, стрелки, шапка), `subtle` — приглушённая нейтральная плашка
 * (закрыть, очистить, убрать строку), `ghost` — без подложки.
 *
 * `tonal` из Button здесь нет намеренно: акцентный тинт у иконочной кнопки всегда означает
 * «включено», и это состояние `selected`, а не громкость.
 */
export type IconButtonVariant = 'primary' | 'secondary' | 'subtle' | 'ghost'

/** Ступени шкалы контролов: xs 28 — плотная строка, sm 32 — по умолчанию, md 38 — шапка, lg 44 — плавающая. */
export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg'

/** Последствие действия. `danger` красит **отклик**, а не покой: сорок красных крестиков в таблице — аварийная панель. */
export type IconButtonTone = 'accent' | 'danger'

export type IconButtonProps = {
  /** Глиф. Декоративен: доступное имя даёт `label`. Пара «ступень → размер иконки» — в контракте. */
  icon: ReactNode
  /**
   * Доступное имя, обязательно. Пишется со значением («Убрать позицию: SRV-DL360-G11»):
   * сорок одинаковых «Удалить» в таблице бесполезны.
   */
  label: string
  variant?: IconButtonVariant
  size?: IconButtonSize
  tone?: IconButtonTone
  /** Включено (в избранном, в сравнении): акцентный тинт и `aria-pressed`. */
  selected?: boolean
  /** Кнопка раскрывает меню или поповер: `aria-expanded`. Передан — `aria-pressed` не ставится. */
  expanded?: boolean
  /** Идёт запрос: спиннер вместо глифа, `aria-busy`, действие погашено. Кнопку не выключает. */
  loading?: boolean
  /** Слот счётчика над правым верхним углом. Позиционирует кнопка, счётчик передаётся `decorative`. */
  badge?: ReactNode
  ref?: Ref<HTMLButtonElement>
  /* aria-label исключён: имя задаётся ровно одним способом — пропом label. */
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'color' | 'aria-label'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const VARIANT_CLASS: Record<IconButtonVariant, string | undefined> = {
  primary: s.primary,
  secondary: s.secondary,
  subtle: s.subtle,
  ghost: s.ghost,
}

const SIZE_CLASS: Record<IconButtonSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
  lg: s.lg,
}

const TONE_CLASS: Record<IconButtonTone, string | undefined> = {
  accent: s.toneAccent,
  danger: s.toneDanger,
}

/** Кит: 16px спиннер в круге 32. На самой плотной ступени он ужимается до 14. */
const SPINNER_SIZE: Record<IconButtonSize, SpinnerSize> = {
  xs: 'xs',
  sm: 'sm',
  md: 'sm',
  lg: 'sm',
}

/**
 * Действие без подписи: кебаб строки, стрелки пагинации, крестик модалки, очистка поля,
 * корзина со счётчиком, «в избранное», «наверх».
 *
 * Тег всегда `<button>`, `type` по умолчанию `"button"`. Доступное имя обязательно и приходит
 * пропом `label`: кнопка без имени не компилируется. Загрузка не выключает кнопку — фокус
 * остаётся на элементе, который пользователь только что нажал.
 *
 * Контракт: docs/ui-kit/contracts/icon-button.md
 */
export function IconButton({
  icon,
  label,
  variant = 'secondary',
  size = 'sm',
  tone = 'accent',
  selected,
  expanded,
  loading = false,
  badge,
  type = 'button',
  disabled = false,
  className,
  onClick,
  ref,
  ...rest
}: IconButtonProps) {
  /*
   * Загрузка не ставит нативный disabled: он унёс бы фокус в body с элемента, который
   * пользователь только что нажал. Действие гасится двумя способами сразу — обработчик наружу
   * не идёт, а preventDefault отменяет действие по умолчанию: у type="submit" это единственный
   * способ не отправить форму второй раз.
   */
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault()
      return
    }

    onClick?.(event)
  }

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cx(
        s.root,
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        TONE_CLASS[tone],
        selected && s.selected,
        loading && s.loading,
        className,
      )}
      data-variant={variant}
      data-size={size}
      data-tone={tone}
      data-selected={selected ? 'true' : undefined}
      data-loading={loading ? 'true' : undefined}
      disabled={disabled}
      aria-label={label}
      /*
       * Раскрытие меню ничего не переключает: объявлять кнопку ещё и «нажатой» нельзя.
       * Правило заведено у Chip и здесь соблюдается дословно.
       */
      aria-pressed={expanded === undefined ? selected : undefined}
      aria-expanded={expanded}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
    >
      {/* Спиннер занимает место глифа: круг фиксирован ступенью, геометрия не меняется */}
      <span className={s.glyph}>
        {loading ? <Spinner size={SPINNER_SIZE[size]} tone="current" /> : icon}
      </span>

      {/*
       * Якорь по левому краю плюс сдвиг трансформом: капсула растёт симметрично от угла,
       * и подгонять смещение под «99+» не нужно. Рецепт из docs/ui-kit/contracts/counter-badge.md.
       */}
      {isFilled(badge) ? <span className={s.badge}>{badge}</span> : null}
    </button>
  )
}
